'use client';

import { Breadcrumb } from '@/components/common/breadcrumb';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { PageHeader } from '@/components/common/page-header';
import { ExpandableCard } from '@/components/ui/expandable-card';
import { OIL_CODE_COLORS, type OilCode } from '@/constants/fuel-colors';
import { useOrganization } from '@/contexts/organization-context';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import {
  PetrohqApiResponseListSalesTotalDto,
  SalesTotalDto,
} from '@/lib/api/data-contracts';
import { useFilterStore } from '@/stores/filter-store';
import { useCallback, useEffect, useState } from 'react';
import * as RealGrid from 'realgrid';
import { RealGridReact, RGDataColumn, RGDataField } from 'realgrid-react';

export function SalesTotal() {
  const { toast } = useToast();
  const { getAllStores } = useOrganization();
  // 온디맨드 방식: API 호출 시점에만 필터 상태 읽기

  // 상태 관리
  const [salesTotalData, setSalesTotalData] = useState<SalesTotalDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // 판매구분별집계 데이터 가져오기 함수
  const fetchSalesTotalData = useCallback(async () => {
    setIsLoading(true);
    try {
      // API 호출 시점에 현재 필터 상태 읽기
      const filters = useFilterStore.getState();

      const storeCodeList =
        filters.store.length > 0 ? filters.store : getAllStores();

      // 중앙집중식 파라미터 구축 사용
      const params = filters.buildApiParams('salestotal', storeCodeList);

      const response = await apiClient.get('sales/sales-total', {
        searchParams: params,
      });
      const data =
        (await response.json()) as PetrohqApiResponseListSalesTotalDto;

      if (data.result) {
        setSalesTotalData(data.result);
      }
    } catch (error) {
      console.error('Sales total data fetch error:', error);
      toast({
        title: '조회 실패',
        description: '판매구분별집계 데이터를 불러오는 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [getAllStores, toast]);

  // 조회 버튼 클릭 핸들러
  const handleSearch = () => {
    fetchSalesTotalData();
  };

  // 초기 데이터 로드 (필터 변경 시 자동 리로드 제거)
  useEffect(() => {
    fetchSalesTotalData();
  }, []); // 빈 의존성 배열로 변경하여 초기 로드만 수행

  const onInitialized = (grid: RealGrid.GridView) => {
    grid.groupBy(['storeName']);
  };

  return (
    <div className='h-full'>
      <ExpandableCard
        title='판매구분별집계'
        breadcrumb={
          <Breadcrumb items={menuBreadcrumbItems('salestotal')} />
        }
        pageHeader={
          <PageHeader
            onSearch={handleSearch}
            isLoading={isLoading}
          />
        }
        fill
      >
        <div
          className='border rounded-lg flex-1 min-h-0'
        >
          <div className='w-full h-full'>
            <RealGridReact
              gridProps={{
                display: { fitStyle: RealGrid.GridFitStyle.FILL },
                checkBar: { visible: false },
                stateBar: { visible: false },
                edit: { readOnly: true, editable: false },
                footer: { visible: true },
                groupPanel: { visible: true },
              }}
              rows={salesTotalData}
              onInitialized={onInitialized}
            >
              <RGDataField
                fieldName={'compCd'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'compName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'storeCd'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'storeName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'closedt'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'closecnt'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'saleType'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'typeName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'dealCode'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'dealName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'oilType'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'oilName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'count'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'qty'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'amount'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'mdcAmount'}
                dataType={RealGrid.ValueType.NUMBER}
              />

              <RGDataColumn
                fieldName={'storeName'}
                header={{ text: '매장' }}
                width={90}
              />
              <RGDataColumn
                fieldName={'closedt'}
                header={{ text: '영업일' }}
                width={80}
              />
              <RGDataColumn
                fieldName={'closecnt'}
                header={{ text: '차수' }}
                width={60}
                numberFormat={'#,##0'}
              />
              <RGDataColumn
                fieldName={'typeName'}
                header={{ text: '판매구분' }}
                width={90}
                autoFilter={true}
              />
              <RGDataColumn
                fieldName={'dealName'}
                header={{ text: '거래처명' }}
                width={120}
              />
              <RGDataColumn
                fieldName={'oilName'}
                header={{ text: '상품명' }}
                width={80}
                styleCallback={(grid, dataCell) => {
                  const oilType = grid.getValue(dataCell.index?.itemIndex!!, "oilType");
                  return {
                    style: { backgroundColor: OIL_CODE_COLORS[Number(oilType) as OilCode] },
                  };
                }}
              />
              <RGDataColumn
                fieldName={'count'}
                header={{ text: '판매횟수' }}
                width={80}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
                numberFormat={'#,##0'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'qty'}
                header={{ text: '판매량' }}
                width={90}
                footer={{ expression: 'sum', numberFormat: '#,##0.000' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0.000' }}
                numberFormat={'#,##0.000'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'amount'}
                header={{ text: '판매금액' }}
                width={100}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
                numberFormat={'#,##0'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'mdcAmount'}
                header={{ text: '할인액' }}
                width={90}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
                numberFormat={'#,##0'}
                styleName={'text-right'}
              />
            </RealGridReact>
          </div>
        </div>

        <div className='mt-4 flex items-center justify-between'>
          <div className='flex items-center space-x-4'>
            <span className='text-sm text-muted-foreground'>
              총 {salesTotalData.length}건
            </span>
            {isLoading && (
              <span className='text-sm text-blue-600'>로딩 중...</span>
            )}
          </div>
        </div>
      </ExpandableCard>
    </div>
  );
}
